/* eslint-disable no-else-return */

/* eslint-disable no-shadow */
import React, { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { Formik } from 'formik';
import { UnmountClosed } from 'react-collapse';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { getUpsellCmsKeyPath } from 'Components/utils/activities/activities.utils';
import {
  RENTAL,
  activityOrEquipementDetailClick,
  buildAttendeesReservationsFromSelectedSchedules,
  getSlotKey,
} from 'Components/widgets/reservation-block/reservationBlock.utils';
import { useAppContext } from 'Context/ReactAppContext';
import { ServicesContext } from 'Context/ServicesContext';
import { t } from 'Src/i18n';
import { formatAmountForLocale } from 'Utils/currency-config-for-locale/currencyConfigForLocale';
import type { ServiceMutationType } from 'Utils/graphql-query-helper/booking/proposal/proposalQuery';
import type { ReservationBlockFormValues, ReservationSlotFormEntry } from 'Src/types/reservation';
import 'Utils/graphql-query-helper/booking/proposal/proposalQuery';
import { Button } from 'Components/frameworks/button/Button';
import { TemplateString } from 'Components/frameworks/template-string/TemplateString';
import { ReservationBlockHeader } from 'Components/widgets/reservation-block/ReservationBlockHeader';
import { ReservationAttendeesStep } from 'Components/widgets/reservation-block/ReservationAttendeesStep';
import { ReservationScheduleStep } from 'Components/widgets/reservation-block/ReservationScheduleStep';
import { ReservationSlotsStep } from 'Components/widgets/reservation-block/ReservationSlotsStep';
import { partition } from 'lodash';
import { ReservationScheduleStepFSS } from 'Components/widgets/reservation-block/ReservationScheduleStepFSS';
export type ReservationBlockProps = {
  headerLabel: string;
  introductionLabel: string;
  onSubmit: (
    arg0: ServiceMutationType,
    arg1: ReadonlyArray<string>,
    arg2: ReadonlyArray<string>,
  ) => any;
  rentButtonLabel: string;
  service: PricingInfoWithScheduleType;
  attendeeId?: string;
  showOnlyCurrentAttendeeSlots?: boolean;
  isButtonLoading?: boolean;
};
const TIMESLOT_REGEX = /([a-z0-9_]+)-([0-9]{8})-([0-9]{8})-([a-z])/;
export const STEPS = {
  ATTENDEES: 'ATTENDEES',
  SCHEDULES: 'SCHEDULES',
  SLOTS: 'SLOTS',
};

const ButtonContent = ({
  hasMultiSchedules,
  showStep,
  cancellingActivity,
  serviceType,
  totalPrice,
  rentButtonLabel,
  locale,
  currency,
}) => {
  if (hasMultiSchedules && showStep === STEPS.ATTENDEES && !cancellingActivity) {
    return t(`booking.activities.${getUpsellCmsKeyPath(serviceType)}.chooseWhen`);
  }

  if (totalPrice > 0) {
    return (
      <TemplateString
        template={rentButtonLabel}
        values={{
          price: formatAmountForLocale(totalPrice, locale, currency),
        }}
      />
    );
  }

  return t('booking.common.validate');
};

export const ReservationBlock = ({
  headerLabel,
  introductionLabel,
  onSubmit,
  rentButtonLabel,
  service,
  showOnlyCurrentAttendeeSlots,
  attendeeId,
  isButtonLoading = false,
}: ReservationBlockProps) => {
  const appContext = useAppContext();
  const { currency, departureDate, arrivalDate } = useContext(ServicesContext);
  const initialShowStep = showOnlyCurrentAttendeeSlots ? STEPS.SCHEDULES : STEPS.ATTENDEES;
  const [showStep, setShowStep] = useState(initialShowStep);
  const [cancellingActivity, setCancellingActivity] = useState(false);
  const formikRef = useRef(null);
  const hasMultiSlots = service.schedules.some(
    (schedule) => schedule.slots.length > 1 && schedule.slots.some((slot) => slot.type !== 'DAY'),
  );
  // THIS IS VERY IMPORTANT AND SHOULD STAY IN PLACE
  // FOR A CASE OF MIXED TYPES OF SCHEDULES IT PUTS THE FSS SCHEDULE AT THE END
  const schedules = service.schedules.sort((a, b) => Number(a.startDate) - Number(b.startDate));
  const serviceType = service.type; // Same type for all services

  const isRental = serviceType === RENTAL;
  const { trackClickEvent } = useTrackEvent();
  const hasMultiSchedules =
    schedules.length > 1 || (schedules.length === 1 && schedules[0]?.slots?.length > 1);
  const { attendees, id: productInfoId } = service;
  const hasReservedSchedule = service.attendees.some((attendee) =>
    attendee.schedules.some(({ isPartiallyRented }) => isPartiallyRented),
  );
  const initialFormValues = {
    sameScheduleForAll: false,
    attendees: attendees.reduce(
      (initialValues, { id, price, schedules }) => ({
        ...initialValues,
        [id]: {
          selected:
            schedules.some((schedule) => schedule.isPartiallyPaid) ||
            schedules.some((schedule) => schedule.isPartiallyRented) ||
            (showOnlyCurrentAttendeeSlots && id === attendeeId),
          isPaid: schedules.every((schedule) => schedule.isFullyPaid),
          price: price.value,
          schedules: schedules.map((schedule) => {
            const commonValues = {
              isPaid: schedule.isPartiallyPaid,
              isFullyPaid: schedule.isFullyPaid,
              startDate: schedule.startDate,
              endDate: schedule.endDate,
              timeSlots: schedule.timeSlots.map((slot) => slot.slotName),
              remainingStock: schedule.timeSlots[0].remainingStock,
            };
            return hasMultiSchedules
              ? {
                  isSelected: schedule.isPartiallyRented || schedule.isPartiallyPaid,
                  ...commonValues,
                }
              : {
                  isSelected: true,
                  ...commonValues,
                };
          }),
        },
      }),
      {},
    ),
    wholeDurationOfStay: attendees.reduce(
      (wholeDurationOfStay, { id, schedules }) => ({
        ...wholeDurationOfStay,
        [id]: schedules.every((schedule) => schedule.isFullyRented),
      }),
      {},
    ),
    slots: attendees.reduce((slots, attendee) => {
      const { id } = attendee;
      return attendee.schedules.reduce((accumulatedSchedules, schedule) => {
        const { startDate, endDate, timeSlots } = schedule;
        return timeSlots.reduce(
          (accumulatedSlots, { slotName, id: serviceId, isPaid, isRented, price }) => {
            accumulatedSlots[
              getSlotKey({
                slotName,
                startDate,
                endDate,
                attendeeId: id,
              })
            ] = {
              productInfoId,
              serviceId,
              isSelected: isPaid || isRented,
              isRented,
              isPaid,
              price: price.value,
            };
            return accumulatedSlots;
          },
          accumulatedSchedules,
        );
      }, slots);
    }, {}),
  };

  const validateForm = (formValues) => {
    const errors = {};

    if (
      showStep === STEPS.ATTENDEES &&
      !cancellingActivity &&
      (!hasReservedSchedule || hasMultiSchedules || hasMultiSlots) &&
      !Object.values(formValues.attendees).some(({ selected, isPaid }) => selected && !isPaid)
    ) {
      errors.attendees = true;
    }

    if (
      hasMultiSchedules &&
      showStep === STEPS.SCHEDULES &&
      (!hasReservedSchedule || hasMultiSlots) &&
      !Object.values(formValues.attendees).every(
        ({ selected, schedules: attendeeSchedules }) =>
          !selected ||
          (selected && Object.values(attendeeSchedules).some(({ isSelected }) => isSelected)) ||
          Object.values(attendeeSchedules).every(({ isFullyPaid }) => isFullyPaid),
      )
    ) {
      errors.schedules = true;
    }

    if (showStep === STEPS.SLOTS) {
      if (
        !hasReservedSchedule &&
        (!Object.entries(formValues.slots).length === 0 ||
          !Object.values(formValues.slots).some(({ isSelected, isPaid }) => isSelected && !isPaid))
      ) {
        errors.slots = true;
      }
    }

    return errors;
  };

  const handleFormSubmit = (values: ReservationBlockFormValues) => {
    if (showOnlyCurrentAttendeeSlots && hasMultiSchedules && !hasMultiSlots) {
      const { attendeesReservations, serviceIds, attendeeIds } =
        buildAttendeesReservationsFromSelectedSchedules(values, productInfoId);
      onSubmit(attendeesReservations, serviceIds, attendeeIds);
      return;
    }

    const serviceIds: string[] = [];
    const attendeeIds: string[] = [];
    const attendeesReservations = Object.entries(values.slots).reduce(
      (attendeesReservations, [slotKey, slot]: [string, ReservationSlotFormEntry]) => {
        const timeSlotRegex = new RegExp(TIMESLOT_REGEX, 'gi');
        const slotMatch = timeSlotRegex.exec(slotKey);

        if (!slotMatch) {
          return attendeesReservations;
        }

        const [, , timeSlotStartDate, timeSlotEndDate, timeSlotCustomerId] = slotMatch;

        if (!attendeeIds.includes(timeSlotCustomerId)) {
          attendeeIds.push(timeSlotCustomerId);
        }

        if (!serviceIds.includes(slot.serviceId)) {
          serviceIds.push(slot.serviceId);
        }

        const attendee = values.attendees[timeSlotCustomerId];

        if (!attendee) {
          return attendeesReservations;
        }

        const currentSchedule = attendee.schedules.find(
          ({ startDate, endDate }) =>
            startDate === timeSlotStartDate && endDate === timeSlotEndDate,
        );

        if (
          (attendee.selected &&
            attendee.schedules.length === 1 &&
            !(schedules[0]?.slots?.length > 1)) ||
          (attendee.selected &&
            currentSchedule?.isSelected &&
            (currentSchedule.timeSlots.length === 1 || slot.isSelected))
        ) {
          const schedule = {
            startDate: timeSlotStartDate,
            endDate: timeSlotEndDate,
            slots: [slot.serviceId],
          };
          const attendeeIndex = attendeesReservations.findIndex(
            ({ customerId }) => customerId === timeSlotCustomerId,
          );

          if (attendeeIndex === -1) {
            attendeesReservations.push({
              customerId: timeSlotCustomerId,
              productInfoId,
              schedules: [schedule],
            });
          } else {
            const scheduleIndex = attendeesReservations[attendeeIndex].schedules.findIndex(
              (schedule) =>
                schedule.startDate === timeSlotStartDate && schedule.endDate === timeSlotEndDate,
            );

            if (scheduleIndex === -1) {
              attendeesReservations[attendeeIndex].schedules.push(schedule);
            } else {
              attendeesReservations[attendeeIndex].schedules[scheduleIndex].slots.push(
                slot.serviceId,
              );
            }
          }
        }

        return attendeesReservations;
      },
      [],
    );
    onSubmit(attendeesReservations, serviceIds, attendeeIds);
  };

  const getTotalPriceReserved = (formValues) => {
    if (
      ((hasMultiSlots && showStep === STEPS.SLOTS) ||
        (!hasMultiSlots && hasMultiSchedules && showStep === STEPS.SCHEDULES) ||
        (!hasMultiSlots && !hasMultiSchedules && showStep === STEPS.ATTENDEES)) &&
      formValues.slots
    ) {
      const filteredAttendees = !showOnlyCurrentAttendeeSlots
        ? service.attendees
        : service.attendees.filter(({ id }) => id === attendeeId);
      return filteredAttendees.reduce((totalPrice, attendee) => {
        return (
          totalPrice +
          attendee.schedules.reduce((priceAttendeeSchedule, schedule) => {
            return (
              priceAttendeeSchedule +
              (schedule.isFullyPaid
                ? 0
                : schedule.timeSlots.reduce((price, { slotName, price: slotPrice }) => {
                    const slotFormValue =
                      formValues.slots[
                        getSlotKey({
                          slotName,
                          startDate: schedule.startDate,
                          endDate: schedule.endDate,
                          attendeeId: attendee.id,
                        })
                      ];
                    const {
                      isPaid: isSlotPaid,
                      isSelected: isSlotSelected,
                      isRented: isSlotRented,
                    } = slotFormValue;
                    const isAttendeeSelected = formValues.attendees[attendee.id].selected;
                    const isScheduleSelected = formValues.attendees[attendee.id].schedules.find(
                      (s) => s.startDate === schedule.startDate && s.endDate === schedule.endDate,
                    )?.isSelected;
                    const isNewSlotSelection = isSlotSelected && !isSlotPaid && !isSlotRented;
                    const attendeePrice =
                      isAttendeeSelected &&
                      isScheduleSelected &&
                      ((!hasMultiSlots && hasMultiSchedules && showStep === STEPS.SCHEDULES) ||
                        (!hasMultiSlots && !hasMultiSchedules && showStep === STEPS.ATTENDEES) ||
                        (hasMultiSlots && isNewSlotSelection))
                        ? slotPrice.value
                        : 0;
                    return price + attendeePrice;
                  }, 0))
            );
          }, 0)
        );
      }, 0);
    }

    return 0;
  };

  const getLowerPrice = (attendee: AttendeeWithSchedulesType): PriceType => {
    if (!(hasMultiSchedules || hasMultiSlots)) {
      return attendee.price;
    }

    let lowerPrice = { ...attendee.price };
    attendee.schedules.forEach((schedule) => {
      schedule.timeSlots.forEach((slot) => {
        if (slot.price.value < lowerPrice.value) {
          lowerPrice = { ...slot.price };
        }
      });
    });
    return lowerPrice;
  };

  const isInitialValid = Object.keys(validateForm(initialFormValues)).length === 0;
  const stepsTotal = hasMultiSlots ? 3 : 2;
  // FULL STAY IN ONE SCHEDULE MODE FSSM
  const [singleDaySchedules, bundledSchedules] = partition(
    schedules,
    ({ startDate, endDate }) => startDate === endDate,
  );
  const shouldActivateFullStayScheduleMode =
    singleDaySchedules.length > 0 && bundledSchedules.length > 0;
  // childcare shows one block per attendee
  const attendeesFiltered =
    showOnlyCurrentAttendeeSlots && attendeeId
      ? attendees.filter(({ id }) => id === attendeeId)
      : attendees;

  useEffect(() => {
    formikRef.current?.validateForm();
  }, [showStep]);

  return (
    <Fragment>
      <header className="z-1 border-b-1 border-lightGrey px-20 py-15 text-16 font-bold leading-tight text-black">
        {headerLabel}
      </header>
      <div className="p-20">
        <Formik
          initialValues={initialFormValues}
          onSubmit={handleFormSubmit}
          validate={validateForm}
          validateOnMount={!isInitialValid}
        >
          {(formik) => {
            formikRef.current = formik;
            const totalPrice = getTotalPriceReserved(formik.values);
            const hasValidationErrors = Object.keys(validateForm(formik.values)).length > 0;
            const hasFewerAttendeesSelected =
              hasReservedSchedule &&
              Object.values(formik.values.attendees).filter(({ selected }) => selected).length <
                Object.values(initialFormValues.attendees).filter(({ selected }) => selected)
                  .length;
            return (
              <Fragment>
                <ReservationBlockHeader
                  isActive={
                    (showStep === STEPS.ATTENDEES && !showOnlyCurrentAttendeeSlots) ||
                    (shouldActivateFullStayScheduleMode && !!attendeeId)
                  }
                  label={introductionLabel}
                  onEdit={() => setShowStep(STEPS.ATTENDEES)}
                  stepNumber={1}
                  stepsTotal={hasMultiSchedules && !showOnlyCurrentAttendeeSlots ? stepsTotal : 1}
                  isEditVisible={!showOnlyCurrentAttendeeSlots}
                />
                {!showOnlyCurrentAttendeeSlots && (
                  <UnmountClosed isOpened={showStep === STEPS.ATTENDEES}>
                    <ReservationAttendeesStep
                      attendees={attendeesFiltered}
                      hasReservedSchedule={hasReservedSchedule}
                      cancellingActivity={cancellingActivity}
                      getLowerPrice={getLowerPrice}
                      setCancellingActivity={setCancellingActivity}
                    />
                  </UnmountClosed>
                )}
                {hasMultiSchedules &&
                  showStep !== STEPS.ATTENDEES &&
                  !showOnlyCurrentAttendeeSlots && (
                    <ReservationBlockHeader
                      isActive={showStep === STEPS.SCHEDULES}
                      label={t(
                        `booking.activities.${getUpsellCmsKeyPath(serviceType)}.registrationDates`,
                      )}
                      onEdit={() => setShowStep(STEPS.SCHEDULES)}
                      stepNumber={2}
                      isEditVisible={!showOnlyCurrentAttendeeSlots}
                      stepsTotal={
                        hasMultiSchedules && !showOnlyCurrentAttendeeSlots ? stepsTotal : 1
                      }
                    />
                  )}
                <UnmountClosed isOpened={showStep === STEPS.SCHEDULES}>
                  {shouldActivateFullStayScheduleMode && attendeeId ? (
                    <ReservationScheduleStepFSS
                      stayDepartureDate={departureDate}
                      stayArrivalDate={arrivalDate}
                      attendeeId={attendeeId}
                    />
                  ) : (
                    <ReservationScheduleStep attendees={attendeesFiltered} />
                  )}
                </UnmountClosed>
                {showStep === STEPS.SLOTS && (
                  <ReservationBlockHeader
                    isActive={showStep === STEPS.SLOTS}
                    label={t('booking.activities.timeSlot.title')}
                    stepNumber={3}
                    stepsTotal={hasMultiSchedules && !showOnlyCurrentAttendeeSlots ? stepsTotal : 1}
                  />
                )}
                <UnmountClosed isOpened={showStep === STEPS.SLOTS}>
                  <ReservationSlotsStep
                    showOnlyCurrentAttendeeSlots={showOnlyCurrentAttendeeSlots}
                    attendeeId={attendeeId}
                  />
                </UnmountClosed>
                <Button
                  isLoading={isButtonLoading}
                  className="mt-20 w-full"
                  data-testid="ReservationBlock-validationBtn"
                  isDisabled={hasValidationErrors || initialFormValues === formik.values}
                  onClick={() => {
                    (totalPrice > 0 || cancellingActivity || hasFewerAttendeesSelected) &&
                      serviceType !== 'CHILDCARE' &&
                      trackClickEvent(TRACK_EVENT_NAMES.CLICK_UPSELL, {
                        detail_click: activityOrEquipementDetailClick(
                          cancellingActivity || hasFewerAttendeesSelected,
                          isRental,
                        ),
                        activity_type: serviceType.toLowerCase(),
                      });

                    if (
                      hasMultiSchedules &&
                      showStep === STEPS.ATTENDEES &&
                      Object.values(formik.values.attendees).some(({ selected }) => selected) &&
                      !cancellingActivity
                    ) {
                      setShowStep(STEPS.SCHEDULES);
                      formik.setErrors({
                        schedules: true,
                      });
                    } else if (showStep === STEPS.SCHEDULES && hasMultiSlots) {
                      setShowStep(STEPS.SLOTS);
                      if (!hasReservedSchedule) {
                        formik.setErrors({
                          slots: true,
                        });
                      }
                    } else if (!formik.dirty && !hasFewerAttendeesSelected) {
                      // if the user has not touch the form we just call onSubmit with no value (to close the pop-in)
                      onSubmit([], [], []);
                    } else {
                      formik.submitForm();
                    }
                  }}
                >
                  <ButtonContent
                    hasMultiSchedules={hasMultiSchedules}
                    showStep={showStep}
                    cancellingActivity={cancellingActivity}
                    serviceType={serviceType}
                    totalPrice={totalPrice}
                    rentButtonLabel={rentButtonLabel}
                    locale={appContext.locale}
                    currency={currency}
                    isButtonLoading={isButtonLoading}
                  />
                </Button>
              </Fragment>
            );
          }}
        </Formik>
      </div>
    </Fragment>
  );
};
